import { productConfig } from "@/config/product";

export const SITE_ORIGIN = (
  import.meta.env.VITE_SITE_ORIGIN as string | undefined
)?.replace(/\/$/, "") || "https://www.floridahoalaw.ai";

export const SITE_NAME = "Florida HOA Law AI";
export const SITE_TAGLINE = "HOA Law Made Simple";
export const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/og-image.png`;
export const TWITTER_HANDLE = "@rethinkhoa";
// HOALaw.ai is the parent brand of the state products; reTHINK HOA is the company.
export const ORGANIZATION_NAME = "HOALaw.ai";
export const ORGANIZATION_ID = "https://www.hoalaw.ai/#organization";

export type SeoMeta = {
  title: string;
  description: string;
  keywords: string;
  noindex: boolean;
};

const BASE_KEYWORDS =
  "Florida HOA law, Florida Statutes Chapter 720, Florida condominium law, Chapter 718, homeowners association, HOA board, Florida HOA AI, reTHINK HOA";

const ROUTES: Record<string, SeoMeta> = {
  "/": {
    title: `${SITE_NAME} | ${SITE_TAGLINE}`,
    description:
      "Get instant, statute-based answers about Florida HOA and condominium law. Built for homeowners, board members, and managers. Not legal advice.",
    keywords: `${BASE_KEYWORDS}, Florida HOA legal information, HOA homeowner rights Florida`,
    noindex: false,
  },
  "/pricing": {
    title: `Pricing | ${SITE_NAME}`,
    description:
      "Plans and pricing for Florida HOA Law AI—Florida HOA and condominium law guidance powered by AI.",
    keywords: `${BASE_KEYWORDS}, HOA AI pricing, Florida HOA subscription`,
    noindex: false,
  },
  "/terms": {
    title: `Terms of Service | ${SITE_NAME}`,
    description:
      "Terms of service for the Florida HOA Law AI web application and subscription.",
    keywords: `${BASE_KEYWORDS}, terms of service`,
    noindex: false,
  },
  "/privacy-policy": {
    title: `Privacy Policy | ${SITE_NAME}`,
    description:
      "How Florida HOA Law AI collects, uses, and protects your personal information.",
    keywords: `${BASE_KEYWORDS}, privacy policy, HOA AI privacy`,
    noindex: false,
  },
  "/login": {
    title: `Sign In | ${SITE_NAME}`,
    description:
      "Sign in to your Florida HOA Law AI account to chat and manage your subscription.",
    keywords: `${BASE_KEYWORDS}, sign in`,
    noindex: true,
  },
  "/signup": {
    title: `Create Account | ${SITE_NAME}`,
    description:
      "Create an account for AI-powered Florida HOA and condominium law guidance.",
    keywords: `${BASE_KEYWORDS}, sign up, HOA AI account`,
    noindex: true,
  },
  "/education/redeem": {
    title: `Education Partner Access | ${SITE_NAME}`,
    description: "Redeem your Education Partner Program complimentary access.",
    keywords: `${BASE_KEYWORDS}`,
    noindex: true,
  },
  "/forgot-password": {
    title: `Forgot Password | ${SITE_NAME}`,
    description: "Reset your password for Florida HOA Law AI.",
    keywords: `${BASE_KEYWORDS}, password reset`,
    noindex: true,
  },
  "/tools": {
    title: `Free Florida HOA Tools | ${SITE_NAME}`,
    description:
      "Free Florida HOA and condominium tools for requesting association records, based on Florida Statutes Chapters 720 and 718.",
    keywords: `${BASE_KEYWORDS}, free HOA tools, HOA records request`,
    noindex: false,
  },
  "/review/start": {
    title: `Free HOA Notice Review | ${SITE_NAME}`,
    description:
      "Upload your HOA notice or violation letter for a free, document-based review based on Florida Statutes Chapters 718 and 720.",
    keywords: `${BASE_KEYWORDS}, free HOA notice review`,
    noindex: true,
  },
  "/generate/start": {
    title: `Generate an HOA Document | ${SITE_NAME}`,
    description:
      "Generate a document for your Florida HOA situation, based on Florida Statutes Chapters 718 and 720.",
    keywords: `${BASE_KEYWORDS}, HOA document generator`,
    noindex: true,
  },
  "/disclaimer": {
    title: `Disclaimer | ${SITE_NAME}`,
    description:
      "Legal disclaimer for Florida HOA Law AI: informational purposes only, not legal advice.",
    keywords: `${BASE_KEYWORDS}, legal disclaimer`,
    noindex: false,
  },
};

const PROTECTED_FALLBACK: SeoMeta = {
  title: SITE_NAME,
  description:
    "Florida HOA and condominium law guidance with AI. Sign in to continue.",
  keywords: BASE_KEYWORDS,
  noindex: true,
};

const NOT_FOUND: SeoMeta = {
  title: `Page Not Found | ${SITE_NAME}`,
  description: "The page you requested could not be found.",
  keywords: BASE_KEYWORDS,
  noindex: true,
};

/**
 * One schema.org Offer per paid plan, read from the pricing config so the structured data
 * always matches the pricing page. Amounts come from `recurringCharge` ("$49.00/year"),
 * the actual billed amount, not the per-month display price.
 */
function planOffers(): Record<string, unknown>[] {
  return productConfig.pricing.plans.flatMap((plan) => {
    const match = plan.autoRenewal.recurringCharge.match(/\$([\d.]+)\/(month|year)/);
    if (!match) return [];
    const [, amount, unit] = match;
    return [
      {
        "@type": "Offer",
        name: `${plan.title} plan`,
        url: `${SITE_ORIGIN}/pricing`,
        price: amount,
        priceCurrency: "USD",
        availability: "https://schema.org/InStock",
        priceSpecification: {
          "@type": "UnitPriceSpecification",
          price: amount,
          priceCurrency: "USD",
          referenceQuantity: { "@type": "QuantitativeValue", value: 1, unitCode: unit === "year" ? "ANN" : "MON" },
        },
      },
    ];
  });
}

export function normalizePathname(pathname: string): string {
  if (!pathname) return "/";
  const trimmed = pathname.replace(/\/+$/, "");
  return trimmed === "" ? "/" : trimmed;
}

export function canonicalUrlForPath(pathname: string): string {
  const p = normalizePathname(pathname);
  // React navigation shares the public Astro tools page canonical.
  if (p === "/tools") return `${SITE_ORIGIN}/tools/`;
  return p === "/" ? `${SITE_ORIGIN}/` : `${SITE_ORIGIN}${p}`;
}

function isProtectedPath(path: string): boolean {
  return (
    path.startsWith("/dashboard") ||
    path.startsWith("/chat") ||
    path.startsWith("/documents") ||
    path.startsWith("/library") ||
    path.startsWith("/account") ||
    path.startsWith("/chatbot") ||
    path.startsWith("/profile") ||
    path.startsWith("/admin")
  );
}

export function getSeoMeta(pathname: string): SeoMeta {
  const path = normalizePathname(pathname);
  if (path === "/404" || path === "*") return NOT_FOUND;
  if (isProtectedPath(path)) return PROTECTED_FALLBACK;
  return ROUTES[path] ?? NOT_FOUND;
}

function normalizeCrumbPath(path: string): string {
  return path === "/" ? "/" : `/${path.replace(/^\/+|\/+$/g, "")}`;
}

function buildBreadcrumbSchema(
  items: Array<{ name: string; path: string }>
): Record<string, unknown> {
  const origin = SITE_ORIGIN.replace(/\/+$/, "");
  const normalized = items.map((item) => ({
    ...item,
    path: normalizeCrumbPath(item.path),
  }));
  const currentPath = normalized[normalized.length - 1]?.path ?? "/";
  const currentUrl =
    currentPath === "/" ? `${origin}/` : `${origin}${currentPath}`;
  return {
    "@type": "BreadcrumbList",
    "@id": `${currentUrl}#breadcrumb`,
    itemListElement: normalized.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.path === "/" ? `${origin}/` : `${origin}${item.path}`,
    })),
  };
}

function getBreadcrumbs(
  pathname: string
): Array<{ name: string; path: string }> {
  const path = normalizePathname(pathname);
  const trails: Record<string, Array<{ name: string; path: string }>> = {
    "/pricing": [
      { name: "Home", path: "/" },
      { name: "Pricing", path: "/pricing" },
    ],
    "/terms": [
      { name: "Home", path: "/" },
      { name: "Terms of Service", path: "/terms" },
    ],
    "/privacy-policy": [
      { name: "Home", path: "/" },
      { name: "Privacy Policy", path: "/privacy-policy" },
    ],
  };
  return trails[path] ?? [];
}

export function buildStructuredData(
  pathname: string
): Record<string, unknown> {
  const path = normalizePathname(pathname);

  const organization = {
    "@type": "Organization",
    "@id": ORGANIZATION_ID,
    name: ORGANIZATION_NAME,
    legalName: "reTHINK HOA",
    url: "https://www.hoalaw.ai/",
    sameAs: ["https://twitter.com/rethinkhoa"],
  };

  const website = {
    "@type": "WebSite",
    "@id": `${SITE_ORIGIN}/#website`,
    url: SITE_ORIGIN,
    name: SITE_NAME,
    description:
      "AI educational tool for understanding Florida HOA and condominium law using authoritative sources.",
    publisher: { "@id": ORGANIZATION_ID },
    inLanguage: "en-US",
  };

  const software = {
    "@type": "SoftwareApplication",
    "@id": `${SITE_ORIGIN}/#software`,
    name: SITE_NAME,
    applicationCategory: "LegalApplication",
    operatingSystem: "Web",
    url: `${SITE_ORIGIN}/`,
    description:
      "Web-based AI educational tool for Florida HOA and condominium law: ask questions about Florida Statutes Chapters 718 and 720.",
    offers: planOffers(),
    provider: { "@id": ORGANIZATION_ID },
    brand: { "@type": "Brand", name: ORGANIZATION_NAME },
  };

  const graph: Record<string, unknown>[] = [organization, website, software];

  const crumbs = getBreadcrumbs(path);
  if (crumbs.length > 1) {
    graph.push(buildBreadcrumbSchema(crumbs));
  }

  return {
    "@context": "https://schema.org",
    "@graph": graph,
  };
}
